// Readable messages for Teams requests. The server's own Teams errors are
// already sentences meant for people ("Team invites aren't available yet.",
// "You don't have access to that in this account."), so they're shown as
// they are; AppSync's and the network's errors get a plain message instead.
// The app's own messages below are in the user's language (localization/).

import { t } from "@/localization";

export const GENERIC_ERROR = t("Something went wrong. Try again.");
export const NO_ACCESS = t("You don't have access to that in this account.");
export const NOT_AVAILABLE = t("Teams isn't available yet.");
export const OFFLINE = t(
  "Couldn't reach EventLive. Check your connection and try again."
);

// The server's own access message, in English whatever the app's language.
const SERVER_NO_ACCESS = /^You don't have access to that in this account\.$/;

// errorType of the Teams Lambdas' errors (eventlive-cdk/teams/src/runtime.ts,
// teamsApi.ts, authorizeMember.ts).
const TEAMS_ERROR_TYPES = new Set([
  "Unauthorized",
  "BadRequest",
  "NotFound",
  "Conflict",
  "LimitExceeded",
  "InvalidInvite",
  "InviteUsed",
  "InviteExpired",
  "InvitesDisabled",
  "NotAvailable",
  "EmailFailed",
  "TeamsUnavailable",
  "TeamsError",
]);

const TECHNICAL =
  /[{}<>[\]_$]|exception|lambda|dynamo|appsync|resolver|mapping|template|undefined|null|stack|errorType|\bat\s+\S+\s+\(|https?:|\bHTTP\b/i;

// A sentence a person can read: short, starts with a capital, ends like a
// sentence, nothing that looks like code or infrastructure.
export const isReadableMessage = (message) =>
  typeof message === "string" &&
  message.length >= 6 &&
  message.length <= 200 &&
  /^[A-Z]/.test(message) &&
  /[.!?]$/.test(message) &&
  !TECHNICAL.test(message);

// The first GraphQL error of an Amplify failure (it throws { data, errors }),
// or the Error itself.
export function firstGraphqlError(err) {
  if (!err) return null;
  if (Array.isArray(err.errors) && err.errors.length) return err.errors[0];
  if (Array.isArray(err) && err.length) return err[0];
  return typeof err === "object" ? err : { message: String(err) };
}

export function teamsErrorMessage(err, fallback = GENERIC_ERROR) {
  const first = firstGraphqlError(err);
  const message =
    typeof first?.message === "string" ? first.message.trim() : "";
  const type = typeof first?.errorType === "string" ? first.errorType : "";

  if (TEAMS_ERROR_TYPES.has(type) && isReadableMessage(message)) return message;
  // The schema doesn't have the Teams fields yet (backend not rolled out).
  if (
    /FieldUndefined|Validation error|Unknown type|is undefined|not defined/i.test(
      message
    )
  ) {
    return NOT_AVAILABLE;
  }
  if (type === "Unauthorized" || /^Not Authorized to access/i.test(message)) {
    return NO_ACCESS;
  }
  if (
    /network ?error|failed to fetch|load failed|network request failed|timed? ?out|aborted/i.test(
      message
    )
  ) {
    return OFFLINE;
  }
  if (isReadableMessage(message)) return message;
  return fallback;
}

// True when the server refused because of access in another account (the
// screen can offer to go back to your own account).
export function isAccessError(err) {
  const first = firstGraphqlError(err);
  return (
    first?.errorType === "Unauthorized" ||
    first?.message === NO_ACCESS ||
    SERVER_NO_ACCESS.test(String(first?.message || "")) ||
    /^Not Authorized to access/i.test(String(first?.message || ""))
  );
}
